/*----------------------------------
- MENU ROW
----------------------------------*/

import { Tag } from '@uniquedomains/components/kit/tag/Tag';
import Icon, { type SpriteIconName } from '@/client/components/ui/icon/Icon';

/*----------------------------------
- TYPES
----------------------------------*/

type MenuRowProps = {
    label: string;
    description: string;
    iconName: SpriteIconName;
    /**
     * Absent marks a destination that does not exist yet. The row then renders as a
     * disabled control with a Soon tag instead of an anchor, so a crawler never counts
     * it and a keyboard user never lands on a page that was never built.
     */
    href?: string;
    /** What assistive technology hears in place of the Soon tag. Required with no `href`. */
    status?: string;
    /** Desktop rows sit in a panel (`.mm .mr`, the tighter row); phone rows (`.msm-list`) draw a 16px icon. */
    density?: 'desktop' | 'mobile';
    onClick?: () => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The icon in the tile, as drawn: 18px on a desktop row (the panel's sheet draws it at 17px), 16px in the phone sheet. */
const ICON_SIZE = { desktop: 18, mobile: 16 } as const;

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The marketing layer's menu row (`.mr`): the grey tile (`.mr-t`, dashed `off` for an unbuilt row), the label over its
 * line (`.mr-b`), nothing at its right. The one row the Domains lists, the Market menu and the Tools menu are built
 * from, on a desktop and in the phone sheet, so the two cannot drift apart. In a desktop panel the row is tighter and
 * its tile turns ink with a butter glyph on hover (`.mm a.mr:hover .mr-t`, web-recheck-06 and 07); the ink tool tiles
 * and the trailing notes left with the redraw of 2026-11-02 (web-recheck-02, 03). A row either links or carries the
 * Soon tag, never both.
 */
export default function MenuRow({
    label,
    description,
    iconName,
    href,
    status,
    density = 'desktop',
    onClick,
}: MenuRowProps) {
    const icon = <Icon name={iconName} size={ICON_SIZE[density]} strokeWidth={1.8} />;
    const body = (
        <span class="mr-b">
            <span class="mr-l">
                {label}
                {href ? null : <Tag label="Soon" />}
            </span>
            <span class="mr-d">{description}</span>
        </span>
    );
    if (!href) {
        return (
            <button type="button" disabled aria-label={`${label}, ${status}`} class="mr">
                <span class="mr-t off">{icon}</span>
                {body}
            </button>
        );
    }

    return (
        <a href={href} onClick={onClick} class="mr">
            <span class="mr-t">{icon}</span>
            {body}
        </a>
    );
}
